import { Chart, ChartConfiguration, Span } from "@goodfill/ui-components";
import { useEffect, useMemo, useRef, useState } from "react";
import { PricedBond } from "../PricedBond";
import { formatCurrencyAmount, formatPercentage } from "@goodfill/format-number";
import { CurrencyComponent } from "../../../currency/CurrencyComponent";
import { Currency, asAmount } from "@goodfill/domain-properties";
import Decimal from "decimal.js";
import { OrderFormState } from "../../order/orderForm/state/OrderFormState";
import { InfoTooltip } from "../../../ui-components/tooltip/InfoTooltip";
import { calculateDiffYears } from "@goodfill/date";
import { formatYearsToMaturity } from "@goodfill/domain-properties-components";

const MIX_CHART_SIZE = 300;
const MAX_CHART_SIZE = 350;
const NUMBER_OF_CHARTS = 3;
const GAP_SIZE_BETWEEN_THEM = 46;

interface Props {
  pricedBond: PricedBond;
  state: OrderFormState;
}

const useChartConfiguration = ({
  cdValue,
  bondValue,
  datasetLabel,
  ticker,
  yAxisTitle,
  yearsToMaturity,
  isCurrency,
  isPercentage,
}: {
  cdValue: number;
  bondValue: number;
  datasetLabel: string;
  ticker: string;
  yAxisTitle?: string;
  yearsToMaturity: string;
  isCurrency?: boolean;
  isPercentage?: boolean;
}): ChartConfiguration =>
  useMemo(
    () => ({
      type: "bar",
      data: {
        labels: [`${yearsToMaturity}Yr CD`, `${ticker} Bond`],
        datasets: [
          {
            label: datasetLabel,
            backgroundColor: ["#215383", "#71CCFF"],
            data: [cdValue, bondValue],
          },
        ],
      },
      options: {
        plugins: {
          tooltip: {
            callbacks: {
              label: (context) => {
                const label = context.dataset.label ?? "";
                const value = Number(context.raw);
                let formattedValue = String(value);

                if (isCurrency) {
                  formattedValue = formatCurrencyAmount({
                    value,
                    numDecimals: 2,
                  });
                }

                if (isPercentage) {
                  formattedValue = formatPercentage({ value, numDecimals: 2 });
                }

                return `${label}: ${formattedValue}`;
              },
            },
            padding: 12,
          },
        },
        scales: {
          x: {
            grid: {
              display: false,
            },
          },
          y: {
            grid: {
              borderDash: [5, 5],
              display: true,
            },
            ticks: isPercentage
              ? {
                  callback: (value) => {
                    return formatPercentage({
                      value,
                      numDecimals: 1,
                      zeroAligned: true,
                    });
                  },
                }
              : undefined,
            title: yAxisTitle
              ? {
                  display: true,
                  text: yAxisTitle,
                  font: {
                    size: 14,
                  },
                }
              : undefined,
          },
        },
      },
    }),
    [cdValue, bondValue, datasetLabel, yAxisTitle, isCurrency, ticker, isPercentage, yearsToMaturity],
  );

export const BondDetailCharts = ({ pricedBond, state }: Props) => {
  const tradeDate = state.i.tradeDate;
  const actualInvestment = asAmount(state.s.faceAmount.amount.mul(pricedBond.askPrice));
  const yieldsToMaturity = calculateDiffYears(pricedBond.maturityDate, tradeDate);

  const cdTotalEarned = asAmount(
    new Decimal(yieldsToMaturity).mul(state.s.faceAmount.amount).mul(pricedBond.interpolatedCDRate),
  );

  const bondTotalEarned = asAmount(
    new Decimal(yieldsToMaturity).mul(state.s.faceAmount.amount).mul(pricedBond.askYield),
  );

  const cdYield = pricedBond.interpolatedCDRate;
  const ticker = pricedBond.issuerTicker;

  const chartsContainerSmallRef = useRef<HTMLDivElement>(null);

  const [chartsContainerSize, setChartsContainerSize] = useState(MIX_CHART_SIZE);

  const chartContainerStyle = useMemo(() => {
    const sumOfGapSpace = (NUMBER_OF_CHARTS - 1) * GAP_SIZE_BETWEEN_THEM;
    const spaceForChart = (chartsContainerSize - sumOfGapSpace) / NUMBER_OF_CHARTS;
    const size = Math.min(MAX_CHART_SIZE, Math.max(MIX_CHART_SIZE, spaceForChart));

    return { height: `${size}px`, width: `${size}px` };
  }, [chartsContainerSize]);

  const yearsToMaturity = calculateDiffYears(pricedBond.maturityDate, tradeDate);

  const totalEarnedChartConfiguration = useChartConfiguration({
    cdValue: cdTotalEarned.toNumber(),
    bondValue: bondTotalEarned.toNumber(),
    datasetLabel: "Total earned",
    yearsToMaturity: formatYearsToMaturity({ value: yearsToMaturity }),
    ticker,
    yAxisTitle: "USD ($)",
    isCurrency: true,
  });

  const yieldToMaturityChartConfiguration = useChartConfiguration({
    cdValue: cdYield.toNumber(),
    bondValue: pricedBond.askYield.toNumber(),
    datasetLabel: "Yield to Maturity",
    yearsToMaturity: formatYearsToMaturity({ value: yearsToMaturity }),
    ticker,
    yAxisTitle: "  ",
    isPercentage: true,
  });

  const earnedVsInvestedChartConfiguration = useChartConfiguration({
    cdValue: cdTotalEarned.div(actualInvestment).toNumber(),
    bondValue: bondTotalEarned.div(actualInvestment).toNumber(),
    datasetLabel: "Earned vs. Invested",
    yearsToMaturity: formatYearsToMaturity({ value: yearsToMaturity }),
    ticker,
    yAxisTitle: " ",
    isPercentage: true,
  });

  const currency = Currency.USD;

  useEffect(() => {
    function handleResize() {
      const { width: widthSmall } = chartsContainerSmallRef.current?.getBoundingClientRect() ?? {
        width: 0,
      };

      setChartsContainerSize(Math.floor(widthSmall));
    }
    handleResize();
    window.addEventListener("resize", handleResize);
    return () => {
      window.removeEventListener("resize", handleResize);
    };
  }, []);

  return (
    <div ref={chartsContainerSmallRef} className="grid lg:grid-cols-3 sm:grid-cols-2 grid-cols-1">
      <div className="items-center sm:items-start lg:w-1/3 sm:w-1/2 w-full md:gap-2 gap-1 md:my-4 my-2 flex flex-col">
        <Span size="lg" font="medium">
          Yield to Maturity
          <InfoTooltip
            text="Yield to Maturity (YTM) of the chosen bond compared to the YTM of the current Marcus
              with-penalty Certificate of Deposit."
          />
        </Span>
        <div style={chartContainerStyle} className="mr-[30px]">
          <Chart configuration={yieldToMaturityChartConfiguration} />
        </div>
      </div>

      <div className="items-center sm:items-start lg:w-1/3 sm:w-1/2 w-full md:gap-2 gap-1 md:my-4 my-2 flex flex-col">
        <Span size="lg" font="medium">
          <CurrencyComponent currency={currency} /> Earned vs. Invested
          <InfoTooltip
            text="Total dollars earned from settlement date
              through maturity, including principal, as a percentage 
              of Actual Investment."
          />
        </Span>
        <div style={chartContainerStyle} className="mr-[30px]">
          <Chart configuration={earnedVsInvestedChartConfiguration} />
        </div>
      </div>

      <div className="items-center sm:items-start lg:w-1/3 sm:w-1/2 w-full md:gap-2 gap-1 md:my-4 my-2 flex flex-col">
        <Span size="lg" font="medium">
          Total <CurrencyComponent currency={currency} /> earned
          <InfoTooltip
            text="Total dollars earned from settlement date
              through maturity, including principal, assuming 
              Actual Investment amount."
          />
        </Span>
        <div style={chartContainerStyle} className="mr-[30px]">
          <Chart configuration={totalEarnedChartConfiguration} />
        </div>
      </div>
    </div>
  );
};
